import { Box, Tab, Tabs, Typography } from "@mui/material";
import { AuthorizedLayout } from "../../component/AuthLayout";
import SecondaryButton from "../../component/SecondaryButton";
import IconAddUser from "../../assets/icons/icon_adduser.svg";
import IconTick from "../../assets/icons/icon_tick_circle.svg";
import { palette } from "../../themes/theme";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import ProfileTab from "./ProfileTab";
import PlanAndUsageTab from "./PlanAndUsageTab";
import BillingHistoryTab from "./BillingHistoryTab";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { mockTime } from "../../utils/helper";
import { showToaster } from "../../redux/slices/toastSlice";
import { hideLoader } from "../../redux/slices/loaderSlice";
import { uploadUserLogo } from "../../api";
import { setProfileImage } from "../../redux/slices/userSlice";
import UserLogo from "../../assets/images/default_logo.png";

const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))(({ theme }) => ({
  paddingLeft: 15,
  borderBottom: "1px solid #ffffff50",
  "& .MuiTabs-indicator": {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiTabs-indicatorSpan": {
    maxWidth: 40,
    width: "100%",
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-selected": {
      color: theme.palette.primary.main,
    },
    "&.Mui-focusVisible": {
      backgroundColor: "rgba(100, 95, 228, 0.32)",
    },
  })
);

const TabPanel = (props) => {
  const { children, value, index, title, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: "40px 40px" }}>
          {title && (
            <Typography sx={{ fontSize: 20, pb: 2 }}>{title}</Typography>
          )}
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const Profile = () => {
  const { state } = useLocation();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  const [value, setValue] = useState(0);
  const [imageUrl, setImageUrl] = useState(user.adminData.profileImageUrl);

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const activeDate = useMemo(() => {
    let oldDate = new Date(user.adminData.activeFrom);
    // Current date
    let currentDate = mockTime();
    // Calculate the difference in milliseconds
    let differenceMs = currentDate.getTime() - oldDate.getTime();
    // Convert milliseconds to days
    let differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
    if (differenceDays === 0) return "Joined Today";
    if (differenceDays < 7)
      return `Joined ${differenceDays} ${
        differenceDays === 1 ? "day" : "days"
      }  ago`;
    if (differenceDays < 30) {
      const weeksAgo = Math.floor(differenceDays / 7);
      return `Joined ${weeksAgo} ${weeksAgo === 1 ? "week" : "weeks"} ago`;
    }
    if (differenceDays < 365) {
      const monthsAgo = Math.floor(differenceDays / 30);
      return `Joined ${monthsAgo} ${monthsAgo === 1 ? "month" : "months"} ago`;
    }
    const yearsAgo = Math.floor(differenceDays / 365);
    return `Joined ${yearsAgo} ${yearsAgo === 1 ? "year" : "years"} ago`;
  }, []);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && file.type !== "image/png" && file.type !== "image/jpeg") {
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: "Only PNG and JPEG format files are allowed!",
          showDialog: true,
          timer: "5000",
        })
      );
      return;
    } else if (file) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload", "profileImageUrl");
        try {
          const res = await uploadUserLogo({
            body: formData,
            config: { headers: { "Content-Type": "multipart/form-data" } },
          });
          dispatch(
            showToaster({
              dialogBgColor: "bg-success",
              dialogMsg: res.data.message,
              showDialog: true,
              timer: "5000",
            })
          );
          if (res.data) {
            dispatch(setProfileImage(res.data.data));
            setImageUrl(res.data.data);
          }
        } catch (err) {
          dispatch(
            showToaster({
              dialogBgColor: "bg-danger",
              dialogMsg: err ?? "Please contact the admin",
              showDialog: true,
              timer: "5000",
            })
          );
        } finally {
          dispatch(hideLoader());
        }
      } catch (error) {
        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: error,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    }
  };

  const handleEditAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger file input click event
    }
  };

  useEffect(() => {
    setValue(state || 0);
  }, [state]);

  return (
    <AuthorizedLayout>
      <Box
        sx={{
          position: "relative",
          width: "80%",
          justifySelf: "center",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", pb: 4 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box
              sx={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                background: palette.primary.main,
                border: `2px solid ${palette.primary.main}`,
              }}
            >
              <img
                src={imageUrl || UserLogo}
                style={{ height: "inherit", width: "inherit" }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 27,
                  pb: 1,
                  fontWeight: "bold",
                }}
              >
                {user.adminData.firstName} {user.adminData.lastName}
              </Typography>
              <SecondaryButton
                variant="outlined"
                fontWeight={100}
                padding={"3px 10px"}
                sx={{ fontSize: 14, border: "1px solid", borderRadius: "4px" }}
                title={"Edit Avatar"}
                img={IconAddUser}
                imgWidth={14}
                hoverImg={IconAddUser}
                onClick={handleEditAvatarClick}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>
          </Box>
          <Box sx={{ textAlign: "end" }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <img src={IconTick} alt="active" />
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 17,
                  pb: 1,
                  fontWeight: "700",
                }}
              >
                Active Account
              </Typography>
            </Box>
            <Typography
              sx={{
                color: "#ffffff80",
                fontSize: 14,
              }}
            >
              {activeDate}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: "100%",
            borderRadius: "12px",
            border: "1px solid #ffffff80",
            background: palette.background.dark,
            // p: "20px 40px 40px 40px",
            color: "white",
          }}
        >
          <StyledTabs
            value={value}
            onChange={handleChange}
            aria-label="styled tabs"
          >
            <StyledTab label="Profile" {...a11yProps(0)} />
            <StyledTab label="Plan and Usage" {...a11yProps(1)} />
            <StyledTab label="Billing History" {...a11yProps(2)} />
          </StyledTabs>
          <TabPanel value={value} index={0} title={"Profile"}>
            <ProfileTab />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <PlanAndUsageTab />
          </TabPanel>
          <TabPanel value={value} index={2} title={"Billing History"}>
            <BillingHistoryTab />
          </TabPanel>
        </Box>
      </Box>
    </AuthorizedLayout>
  );
};

export default Profile;
